import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useCurrencyField } from '@vaptmenu/money';
import { api, describeCommercialError, describeError, newIdempotencyKey } from '@/lib/api';
import { useIdempotencyKey } from '@/lib/idempotency';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { LimitOverrideCheckbox } from '@/components/LimitOverrideCheckbox';
import { useHasPlatformPermission } from '@/stores/auth';

interface Discount {
  id: string;
  tenantId: string;
  subscriptionId: string;
  kind: string;
  value: number;
  startDate: string;
  endDate: string;
  originalSubscriptionValue: number;
  discountedSubscriptionValue: number;
  status: string;
  asaasErrorMessage: string | null;
  reason: string;
  createdAt: string;
  activatedAt: string | null;
  revokedAt: string | null;
}

interface Page<T> {
  items: T[];
  totalPages: number;
}

const statusBadge = (s: string) => {
  switch (s) {
    case 'Active': return 'badge badge-success';
    case 'Pending': return 'badge badge-warning';
    case 'Failed': return 'badge badge-error';
    case 'Revoked':
    case 'Expired': return 'badge';
    default: return 'badge';
  }
};

export function DiscountsPage() {
  const { tenantId } = useParams();
  // Criar e revogar exigem platform.discounts.write na API; a listagem é de
  // todo operador (change platform-backoffice-hardening, C1).
  const canWrite = useHasPlatformPermission('platform.discounts.write');
  const [items, setItems] = useState<Discount[]>([]);
  // Ver AdjustmentsPage: `loading` derivado da busca que ja terminou.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshSeq, setRefreshSeq] = useState(0);

  // create form
  const [kind, setKind] = useState<'Percentage' | 'FixedAmount'>('Percentage');
  // Um campo, dois significados: porcentagem (0-100) ou reais. Só o segundo
  // ganha máscara — mascarar porcentagem faria "50" virar "0,50".
  const [value, setValue] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [limitOverride, setLimitOverride] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const idempotency = useIdempotencyKey();

  const valueField = useCurrencyField({ value, onValueChange: setValue });

  const requestKey = `${tenantId}:${refreshSeq}`;
  const loading = loadedKey !== requestKey;

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    // Mesmo defeito da tela de ajustes: sem `page` o endpoint responde 500.
    api.get<Page<Discount>>(`/platform/tenants/${tenantId}/discounts`, { params: { page: 1, pageSize: 50 } })
      .then(({ data }) => !cancelled && setItems(data.items))
      .catch((err) => !cancelled && setError(describeError(err)))
      .finally(() => !cancelled && setLoadedKey(requestKey));
    return () => { cancelled = true; };
  }, [tenantId, refreshSeq, requestKey]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    const url = `/platform/tenants/${tenantId}/discounts`;
    const body = {
      kind,
      value,
      startDate,
      endDate,
      reason: reason.trim(),
      // Só a exceção do Admin leva a marca; sem ela, o campo nem vai.
      ...(limitOverride ? { limitOverride: true } : {}),
    };
    try {
      // A mesma chave na repetição do mesmo pedido (ver useIdempotencyKey).
      await api.post(url, body, { headers: { 'Idempotency-Key': idempotency.keyFor(url, body) } });
      idempotency.settle();
      setValue(null);
      setReason('');
      setStartDate('');
      setEndDate('');
      setLimitOverride(false);
      setRefreshSeq((s) => s + 1);
    } catch (err) {
      idempotency.settle(err);
      // O teto do desconto vem em %, em R$ (fixo) ou em meses; a API manda a unidade.
      setFormError(describeCommercialError(err, kind === 'Percentage' ? 'percent' : 'currency'));
    } finally {
      setSubmitting(false);
    }
  }

  async function revoke(id: string) {
    if (!confirm('Revogar este desconto? O valor original será restaurado no Asaas.')) return;
    try {
      await api.post(
        `/platform/tenants/${tenantId}/discounts/${id}/revoke`,
        {},
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      setRefreshSeq((s) => s + 1);
    } catch (err) {
      setError(describeError(err));
    }
  }

  return (
    <div className="page page-detail">
      <Link to={`/tenants/${tenantId}`} className="backlink">← Tenant</Link>
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
        Descontos recorrentes
      </h2>

      <div className="info-banner">
        Descontos afetam apenas faturas <strong>futuras</strong>. Para
        descontar a fatura atual, crie também uma{' '}
        <Link to={`/tenants/${tenantId}/adjustments`}>concessão Credit</Link>.
      </div>

      {canWrite && (
        <form className="card" onSubmit={onCreate} style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 16 }}>Novo desconto</h3>
          {formError && <div className="error-banner">{formError}</div>}
          <div className="grid-2">
            <div className="field">
              <label htmlFor="kind">Tipo</label>
              <select
                id="kind"
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as typeof kind);
                  // 50% e R$ 50,00 são coisas diferentes: trocar o tipo sem
                  // limpar deixaria o número anterior com outro sentido.
                  setValue(null);
                }}
              >
                <option value="Percentage">% sobre o valor</option>
                <option value="FixedAmount">R$ fixo</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="value">Valor ({kind === 'Percentage' ? '% (0-100)' : 'R$'})</label>
              {kind === 'Percentage' ? (
                <input
                  id="value" type="number" step="0.01" min="0.01" max="100" required
                  value={value ?? ''}
                  onChange={(e) => setValue(e.target.value === '' ? null : Number(e.target.value))}
                />
              ) : (
                <input id="value" placeholder="0,00" required {...valueField} />
              )}
            </div>
            <div className="field">
              <label htmlFor="startDate">Início</label>
              <input id="startDate" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="endDate">Fim</label>
              <input id="endDate" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
            </div>
          </div>
          <div className="field">
            <label htmlFor="reason">Motivo</label>
            <textarea id="reason" rows={2} required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <LimitOverrideCheckbox checked={limitOverride} onChange={setLimitOverride} />
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? 'Criando…' : 'Criar desconto'}
          </button>
        </form>
      )}

      {error && <div className="error-banner">{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-scroll">
          <CardTable>
            <THead>
              <Tr>
                <Th>Quando</Th><Th>Tipo / valor</Th><Th>Janela</Th><Th>Status</Th>
                <Th>Original → Desc.</Th><Th>Motivo</Th><Th></Th>
              </Tr>
            </THead>
            <TBody>
              {loading && <Tr><Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">Carregando…</Td></Tr>}
              {!loading && items.length === 0 && (
                <Tr><Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">Sem descontos neste tenant.</Td></Tr>
              )}
              {!loading && items.map((d) => (
                <Tr key={d.id}>
                  <Td heading>{new Date(d.createdAt).toLocaleString('pt-BR')}</Td>
                  <Td label="Tipo / valor">
                    <span className="badge">{d.kind}</span>{' '}
                    {d.kind === 'Percentage' ? `${d.value}%` : `R$ ${d.value.toFixed(2)}`}
                  </Td>
                  <Td label="Janela" className="mono" style={{ fontSize: 12 }}>
                    {d.startDate} → {d.endDate}
                  </Td>
                  <Td label="Status">
                    <span className={statusBadge(d.status)}>{d.status}</span>
                    {d.asaasErrorMessage && (
                      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{d.asaasErrorMessage}</div>
                    )}
                  </Td>
                  <Td label="Original → Desc.">R$ {d.originalSubscriptionValue.toFixed(2)} → R$ {d.discountedSubscriptionValue.toFixed(2)}</Td>
                  <Td label="Motivo" style={{ maxWidth: 200 }}>{d.reason}</Td>
                  <Td action>
                    {canWrite && (d.status === 'Active' || d.status === 'Pending') && (
                      <button
                        className="btn-secondary btn"
                        style={{ padding: '6px 10px', fontSize: 12 }}
                        onClick={() => revoke(d.id)}
                      >Revogar</button>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
        </div>
      </div>
    </div>
  );
}
